import { CheckCircle2, Clock, MapPin, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { ChoiceCard } from "../ChoiceCard";
import { StepTitle } from "./AboutSteps";
import { structureOptions } from "@/lib/onboarding/defaults";
import { momentKey } from "@/lib/onboarding/labels";
import { MOMENT_SLOTS, type CombinedAnswer, type MomentAnswer, type MomentSlot, type OnboardingAnswers } from "@/lib/onboarding/types";

/* ------------------------------------------------------------- W04-WB-A */

export function CombinedStep({ value, onSelect }: { value: CombinedAnswer | null; onSelect: (value: CombinedAnswer) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <StepTitle>{t("onboarding.combined.title")}</StepTitle>
      <div className="space-y-3" role="radiogroup" aria-labelledby="onboarding-step-title">
        {(["together", "separate"] as CombinedAnswer[]).map((option) => (
          <ChoiceCard
            key={option}
            selected={value === option}
            onSelect={() => onSelect(option)}
            title={t(`onboarding.combined.${option}`)}
            testId={`onboarding-combined-${option}`}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="link"
        className="mt-4 h-auto p-0 text-pink-600"
        aria-pressed={value === "none"}
        onClick={() => onSelect("none")}
        data-testid="onboarding-combined-none"
      >
        {t("onboarding.combined.none")}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ W04 */

interface StructureStepProps {
  answers: OnboardingAnswers;
  invalid: boolean;
  onToggle: (slot: MomentSlot) => void;
}

export function StructureStep({ answers, invalid, onToggle }: StructureStepProps) {
  const { t } = useTranslation();
  const options = structureOptions(answers.type);
  // For wedding + baptism the church part is already decided by the previous screen.
  const fixed = answers.type === "wedding_baptism"
    ? MOMENT_SLOTS.filter((slot) => !options.includes(slot) && answers.moments[slot].enabled)
    : [];
  return (
    <div className="space-y-3">
      <StepTitle>{t(`onboarding.structure.title.${answers.type}`)}</StepTitle>
      {fixed.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {t("onboarding.structure.includes", {
            moments: fixed.map((slot) => t(`onboarding.moments.${momentKey(slot, answers)}`)).join(", "),
          })}
        </p>
      )}
      <div className="space-y-3" role="group" aria-labelledby="onboarding-step-title">
        {options.map((slot) => (
          <ChoiceCard
            key={slot}
            role="checkbox"
            selected={answers.moments[slot].enabled}
            onSelect={() => onToggle(slot)}
            title={t(`onboarding.moments.${momentKey(slot, answers)}`)}
            testId={`onboarding-moment-${slot}`}
          />
        ))}
      </div>
      {invalid && <p className="text-sm text-destructive">{t("onboarding.structure.atLeastOne")}</p>}
      <p className="text-sm text-muted-foreground">{t("onboarding.structure.hint")}</p>
    </div>
  );
}

/* --------------------------------------------------------------- W05A/B */

function PartCounter({ part }: { part: "time" | "location" }) {
  const { t } = useTranslation();
  return (
    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-pink-600">
      {t("onboarding.partOf", { current: part === "time" ? 1 : 2, total: 2 })}
    </p>
  );
}

export function AddedChip({ slot, answers }: { slot: MomentSlot; answers: OnboardingAnswers }) {
  const { t } = useTranslation();
  const moment = answers.moments[slot];
  return (
    <div
      className="mb-6 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
      role="status"
      data-testid="onboarding-added-chip"
    >
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        <strong>{t(`onboarding.moments.${momentKey(slot, answers)}`)}</strong>
        {[moment.time, moment.venue].filter(Boolean).map((part) => ` · ${part}`)}
        {" — "}
        {t("onboarding.location.added")}
      </span>
    </div>
  );
}

interface MomentTimeStepProps {
  slot: MomentSlot;
  answers: OnboardingAnswers;
  invalid: boolean;
  onChange: (time: string) => void;
}

export function MomentTimeStep({ slot, answers, invalid, onChange }: MomentTimeStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <PartCounter part="time" />
      <StepTitle>{t(`onboarding.time.title.${momentKey(slot, answers)}`)}</StepTitle>
      <div className="space-y-1.5">
        <Label htmlFor="onboarding-time" className="text-base">
          {t("onboarding.time.label")}
        </Label>
        <Input
          id="onboarding-time"
          type="time"
          step={300}
          value={answers.moments[slot].time ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 max-w-[12rem] text-lg"
          aria-invalid={invalid || undefined}
          data-testid="onboarding-time"
          autoFocus
        />
        {invalid && <p className="text-sm text-destructive">{t("onboarding.time.invalid")}</p>}
      </div>
      {!answers.date && <p className="text-sm text-muted-foreground">{t("onboarding.time.noDateNote")}</p>}
    </div>
  );
}

interface MomentLocationStepProps {
  slot: MomentSlot;
  answers: OnboardingAnswers;
  /** The previous moment that already has a place (for "same place?"). */
  sameCandidate: MomentSlot | null;
  sameAs: MomentSlot | null;
  onSameAs: (slot: MomentSlot | null) => void;
  onChange: (changes: Partial<Pick<MomentAnswer, "venue" | "address" | "city">>) => void;
}

export function MomentLocationStep({ slot, answers, sameCandidate, sameAs, onSameAs, onChange }: MomentLocationStepProps) {
  const { t } = useTranslation();
  const moment = answers.moments[slot];
  const field = (key: "venue" | "address" | "city", label: string, placeholder?: string, autoFocus?: boolean) => (
    <div className="space-y-1.5">
      <Label htmlFor={`onboarding-${key}`} className="text-base">
        {label}
      </Label>
      <Input
        id={`onboarding-${key}`}
        value={moment[key] ?? ""}
        onChange={(e) => onChange({ [key]: e.target.value })}
        placeholder={placeholder}
        className="h-12 text-lg"
        maxLength={255}
        autoComplete={key === "address" ? "street-address" : key === "city" ? "address-level2" : "off"}
        data-testid={`onboarding-${key}`}
        autoFocus={autoFocus}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <PartCounter part="location" />
      <StepTitle>{t(`onboarding.location.title.${momentKey(slot, answers)}`)}</StepTitle>

      {sameCandidate && (
        <div className="space-y-3" role="radiogroup" aria-label={t("onboarding.location.sameQuestion", { moment: t(`onboarding.moments.${momentKey(sameCandidate, answers)}`).toLowerCase() })}>
          <p className="text-base font-medium">
            {t("onboarding.location.sameQuestion", { moment: t(`onboarding.moments.${momentKey(sameCandidate, answers)}`).toLowerCase() })}
          </p>
          <ChoiceCard
            selected={sameAs === sameCandidate}
            onSelect={() => onSameAs(sameCandidate)}
            title={t("onboarding.location.sameYes")}
            description={[answers.moments[sameCandidate].venue, answers.moments[sameCandidate].city].filter(Boolean).join(", ")}
            testId="onboarding-same-yes"
          />
          <ChoiceCard selected={sameAs === null} onSelect={() => onSameAs(null)} title={t("onboarding.location.sameNo")} testId="onboarding-same-no" />
        </div>
      )}

      {sameAs === null && (
        <div className="space-y-4">
          {field("venue", t("onboarding.location.venue"), t(`onboarding.location.venuePlaceholders.${momentKey(slot, answers)}`), !sameCandidate)}
          {field("address", t("onboarding.location.address"))}
          {field("city", t("onboarding.location.city"))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ W06 */

export function RecapStep({ answers, onEdit }: { answers: OnboardingAnswers; onEdit: (slot: MomentSlot | null) => void }) {
  const { t } = useTranslation();
  const enabled = MOMENT_SLOTS.filter((slot) => answers.moments[slot].enabled);
  return (
    <div className="space-y-4">
      <StepTitle>{t("onboarding.recap.title")}</StepTitle>
      {enabled.length === 0 && <p className="text-muted-foreground">{t("onboarding.recap.empty")}</p>}
      <ul className="space-y-3">
        {enabled.map((slot) => {
          const moment = answers.moments[slot];
          const place = [moment.venue, moment.address, moment.city].filter(Boolean).join(", ");
          return (
            <li key={slot} className="flex items-start justify-between gap-3 rounded-2xl border bg-background p-4" data-testid={`onboarding-recap-${slot}`}>
              <div className="min-w-0 space-y-1">
                <p className="font-semibold">{t(`onboarding.moments.${momentKey(slot, answers)}`)}</p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  {moment.time ?? t("onboarding.recap.timeLater")}
                </p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  {place || t("onboarding.recap.placeLater")}
                </p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(slot)} data-testid={`onboarding-recap-edit-${slot}`}>
                <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {t("onboarding.recap.edit")}
              </Button>
            </li>
          );
        })}
      </ul>
      <Button type="button" variant="link" className="h-auto p-0 text-pink-600" onClick={() => onEdit(null)}>
        {t("onboarding.recap.changeMoments")}
      </Button>
    </div>
  );
}
